'use client'

import { Button } from '@/components/ui/button' // hoặc bạn dùng nút thường cũng được
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { sanitize } from '@/lib/helpers/sanitize'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

interface ProductDescriptionExpandableProps {
  htmlContent: string
}

export const ProductDescriptionExpandable = ({
  htmlContent,
}: ProductDescriptionExpandableProps) => {
  const isMobile = useIsMobileClient()
  const [expanded, setExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)
  const [maxHeight, setMaxHeight] = useState('0px')
  const contentRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const t = useTranslations('button')
  // Helper để cập nhật trạng thái toggle và maxHeight
  const updateToggleState = () => {
    const el = contentRef.current
    if (!el) return
    const fullHeight = el.scrollHeight
    const initHeight = isMobile ? 100 : 200

    if (fullHeight < initHeight) {
      setShowToggle(false)
      setMaxHeight('none')
      return
    }
    setShowToggle(fullHeight > initHeight)
    setMaxHeight(expanded ? 'none' : `${initHeight}px`)
  }

  useEffect(() => {
    updateToggleState()
    // Lắng nghe sự kiện load của ảnh bên trong content
    const el = contentRef.current
    if (!el) return
    const images = Array.from(el.getElementsByTagName('img'))
    if (images.length === 0) return
    let loadedCount = 0
    const onImgLoad = () => {
      loadedCount++
      if (loadedCount === images.length) {
        updateToggleState()
      }
    }
    images.forEach((img) => {
      if (img.complete) {
        loadedCount++
      } else {
        img.addEventListener('load', onImgLoad)
        img.addEventListener('error', onImgLoad)
      }
    })
    // Nếu tất cả ảnh đã load sẵn
    if (loadedCount === images.length) {
      updateToggleState()
    }
    return () => {
      images.forEach((img) => {
        img.removeEventListener('load', onImgLoad)
        img.removeEventListener('error', onImgLoad)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent, expanded, isMobile])

  // Theo dõi thay đổi DOM bên trong content (ví dụ: ảnh được thêm động)
  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new MutationObserver(() => {
      updateToggleState()
    })
    observerRef.current.observe(el, { childList: true, subtree: true })
    return () => {
      observerRef.current?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent, expanded, isMobile])

  const scrollWithOffset = () => {
    const offset = 200
    const top = wrapperRef.current?.getBoundingClientRect().top ?? 0
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    window.scrollTo({
      top: top + scrollTop - offset,
      behavior: 'smooth',
    })
  }

  const handleToggle = () => {
    if (expanded && wrapperRef.current) {
      scrollWithOffset()
      setTimeout(() => {
        setExpanded(false)
      }, 260)
    } else {
      setExpanded(true)
    }
  }

  return (
    <div className='relative mt-6 text-sm lg:mt-[74px]' ref={wrapperRef}>
      <div
        className='relative overflow-hidden transition-[max-height] duration-500 ease-in-out'
        style={{ maxHeight }}
      >
        <div
          ref={contentRef}
          className='rich-html w-full'
          dangerouslySetInnerHTML={{ __html: sanitize(htmlContent) }}
        />
        {!expanded && showToggle && (
          <div
            className='pointer-events-none absolute bottom-0 left-0 right-0 h-full'
            style={{
              background:
                'linear-gradient(180deg, rgba(242, 242, 242, 0) -28.68%, #F2F2F2 70.51%)',
            }}
          />
        )}
      </div>
      {showToggle && (
        <div className={expanded ? 'sticky bottom-28 z-[1]' : ''}>
          <div className='text-center'>
            <Button
              variant='outline-neutral'
              onClick={handleToggle}
              className='text-sm'
            >
              {expanded ? t('collapse') : t('view_more')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
